import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { PROPERTY_FAQ_CATEGORIES } from "@/content/property-corpus/faq-defs";

export const Route = createFileRoute("/property/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/property-corpus");
    return { faqs: m.PROPERTY_FAQ_TH };
  },
  component: PropertyFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `ซื้ออสังหาฯ–โอนกรรมสิทธิ์ต่างชาติ — FAQ ${faqs.length} ข้อ`,
        description:
          "คำถามที่พบบ่อยเรื่องชาวต่างชาติซื้อคอนโด (โควตา 49%) ที่ดินและบ้าน สัญญาเช่า 30 ปี สิทธิเก็บกิน โฉนดและการตรวจสอบ ค่าธรรมเนียม–ภาษีวันโอนที่กรมที่ดิน หนังสือมอบอำนาจ ท.ด.21/อ.ช.21 นอมินี คู่สมรสและมรดก",
        path: "/property/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/property/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "อสังหาริมทรัพย์ FAQ", url: `${site}/property/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/property/faq")) },
      ],
    };
  },
});

function PropertyFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>อสังหาริมทรัพย์ FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          ซื้ออสังหาริมทรัพย์และโอนกรรมสิทธิ์สำหรับชาวต่างชาติ — คำถามที่พบบ่อย {faqs.length} ข้อ
        </h1>
        <p className="mb-6 text-muted-foreground">
          คำตอบอ้างอิงพระราชบัญญัติอาคารชุด พ.ศ. 2522 (แก้ไขเพิ่มเติม) ประมวลกฎหมายที่ดิน ประมวลกฎหมายแพ่งและพาณิชย์
          ระเบียบและอัตราค่าธรรมเนียมของกรมที่ดิน (dol.go.th) กรมสรรพากร (rd.go.th) และประกาศธนาคารแห่งประเทศไทย (bot.or.th)
          เรื่องการนำเงินตราต่างประเทศเข้าประเทศ ข้อมูลนี้เป็นข้อมูลทั่วไป ไม่ใช่ความเห็นทางกฎหมายเฉพาะกรณี
          และไม่รับประกันผลการพิจารณาของเจ้าพนักงานที่ดิน
        </p>
        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/property/faq" className="text-primary underline">Foreign property ownership &amp; conveyancing FAQ →</a>
        </div>
        <section aria-label="หมวดคำถาม" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">เลือกอ่านตามหมวด</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {PROPERTY_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/property/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleTh}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descTh}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">ข้อ {i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <section className="mt-12">
          <h2 className="mb-3 text-xl font-bold">บริการที่เกี่ยวข้อง</h2>
          <ul className="grid gap-2 text-sm md:grid-cols-2">
            <li><a className="text-primary underline" href="/notary/faq">Notary Public / หนังสือมอบอำนาจ</a></li>
            <li><a className="text-primary underline" href="/translation/faq">แปลเอกสารรับรอง</a></li>
            <li><a className="text-primary underline" href="/consular-legalization/faq">นิติกรณ์กรมการกงสุล</a></li>
            <li><a className="text-primary underline" href="/marriage/faq">จดทะเบียนสมรสไทย–ต่างชาติ</a></li>
          </ul>
        </section>
        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ปรึกษาทีมงาน</h2>
          <p className="text-sm text-muted-foreground">
            สอบถามรายการเอกสาร ลำดับขั้นตอนที่สำนักงานที่ดิน และกรอบเวลาได้ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อเจ้าหน้าที่</a>
        </section>
        <FaqSourceNote topic="property" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}
